import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { shortDate } from "@/lib/format";
import Seo from "@/components/Seo";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ChevronLeft, CalendarCheck, CalendarClock, ClipboardList, ShieldCheck, Sparkles,
  Camera, Paperclip, BarChart3, Lock, Mail, Globe, ArrowLeftRight, Send, Copy, Check,
} from "lucide-react";

function Section({ icon: Icon, title, children, testId, action }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-5" data-testid={testId}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" />
          <p className="text-sm font-semibold text-foreground">{title}</p>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function SessionRow({ b, t }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{b.offering_title || t("cd.session_fallback")}</p>
        <p className="text-xs text-muted-foreground">{b.scheduled_at ? shortDate(b.scheduled_at) : "—"}</p>
      </div>
      <span className="shrink-0 rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold text-muted-foreground">{b.status}</span>
    </div>
  );
}

function SnapshotCompare({ clientId, t }) {
  const [cmp, setCmp] = useState(null);
  const [requesting, setRequesting] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/coach/snapshot-comparison/${clientId}`); setCmp(data); }
    catch { setCmp({ available: false, count: 0 }); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const requestSnapshot = async () => {
    setRequesting(true);
    try {
      await api.post("/snapshot-requests", { client_id: clientId });
      toast.success(t("cd.snap_requested"));
      await load();
    } catch (err) { toast.error(apiError(err, t("cd.snap_request_failed"))); }
    finally { setRequesting(false); }
  };

  const d = cmp?.delta || {};
  const chips = [
    { key: "new", n: d.beliefs_new_count ?? 0, cls: "bg-emerald-100 text-emerald-700" },
    { key: "dissolved", n: d.beliefs_dissolved_count ?? 0, cls: "bg-rose-100 text-rose-700" },
    { key: "recurring", n: d.beliefs_recurring_count ?? 0, cls: "bg-secondary text-muted-foreground" },
  ];

  return (
    <Section icon={ArrowLeftRight} title={t("cd.snap_title")} testId="cd-snapshot-compare"
      action={<button onClick={requestSnapshot} disabled={requesting} data-testid="cd-snap-request" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"><Send className="h-3.5 w-3.5" /> {requesting ? t("cd.snap_requesting") : t("cd.snap_request")}</button>}>
      {cmp === null ? (
        <Skeleton className="h-16 rounded-xl" />
      ) : !cmp.available ? (
        <p className="text-sm text-muted-foreground" data-testid="cd-snap-empty">
          {t("cd.snap_need_more", { count: cmp.count ?? 0 })}
        </p>
      ) : (
        <div className="space-y-3">
          {d.days_between != null && <p className="text-xs text-muted-foreground">{t("cd.snap_days_between", { n: d.days_between })}</p>}
          <div className="flex flex-wrap gap-2">
            {chips.map((c) => (
              <span key={c.key} className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${c.cls}`} data-testid={`cd-snap-beliefs-${c.key}`}>
                {t(`cd.snap_beliefs_${c.key}`, { n: c.n })}
              </span>
            ))}
          </div>
          {(d.pillars_shifted || []).length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("cd.snap_pillars_shifted")}</p>
              {d.pillars_shifted.map((p, i) => (
                <p key={i} className="text-sm text-foreground">
                  <span className="font-medium">{t(`cd.snap_pillar_${p.pillar}`)}:</span> <span className="text-muted-foreground">{p.from_heading} → {p.to_heading}</span>
                </p>
              ))}
            </div>
          )}
          {d.practice_shifted && (
            <p className="text-sm text-foreground"><span className="font-medium">{t("cd.snap_practice_shift")}:</span> <span className="text-muted-foreground">{d.practice_from} → {d.practice_to}</span></p>
          )}
          <div className="grid gap-2 sm:grid-cols-2">
            {cmp.previous && (
              <div className="rounded-xl border border-border bg-card p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("cd.snap_previous")}{cmp.previous.created_at ? ` · ${shortDate(cmp.previous.created_at)}` : ""}</p>
                {cmp.previous.narrative && <p className="mt-1 line-clamp-4 text-xs text-foreground">{cmp.previous.narrative}</p>}
              </div>
            )}
            {cmp.latest && (
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">{t("cd.snap_latest")}{cmp.latest.created_at ? ` · ${shortDate(cmp.latest.created_at)}` : ""}</p>
                {cmp.latest.narrative && <p className="mt-1 line-clamp-4 text-xs text-foreground">{cmp.latest.narrative}</p>}
              </div>
            )}
          </div>
        </div>
      )}
    </Section>
  );
}

function EvaNudge({ clientId, t }) {
  const [draft, setDraft] = useState("");
  const [theme, setTheme] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/coach/eva-nudge/${clientId}`, { tone: "warm", include_practice: true });
      setDraft(data?.draft || "");
      setTheme(data?.latest_theme || "");
    } catch (err) { toast.error(apiError(err, t("nudge.failed"))); }
    finally { setLoading(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(draft); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* noop */ }
  };

  return (
    <Section icon={Sparkles} title={t("nudge.title")} testId="cd-eva-nudge"
      action={<button onClick={generate} disabled={loading} data-testid="cd-nudge-generate" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"><Sparkles className="h-3.5 w-3.5" /> {loading ? t("nudge.generating") : (draft ? t("nudge.regenerate") : t("nudge.generate"))}</button>}>
      {!draft ? (
        <p className="text-sm text-muted-foreground">{t("nudge.hint")}</p>
      ) : (
        <div className="space-y-2">
          {theme && <p className="text-xs text-muted-foreground">{t("nudge.theme")}: <span className="font-medium text-foreground">{theme}</span></p>}
          <Textarea className="min-h-[120px]" value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="cd-nudge-text" />
          <div className="flex justify-end">
            <Button size="sm" variant="outline" className="gap-1.5 rounded-full" onClick={copy} data-testid="cd-nudge-copy">
              {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />} {t("nudge.copy")}
            </Button>
          </div>
          <p className="text-[11px] italic text-muted-foreground">{t("nudge.send_hint")}</p>
        </div>
      )}
    </Section>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const t = useT();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [sharedInfo, setSharedInfo] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.get(`/coach/clients/${id}`).then((r) => r.data);
      setDetail(d);
      const [si, an] = await Promise.all([
        api.get(`/coach/clients/${id}/shared-info`).then((r) => r.data).catch(() => null),
        api.get(`/coach/clients/${id}/analysis`).then((r) => r.data).catch(() => null),
      ]);
      setSharedInfo(si);
      setAnalysis(an);
    } catch (err) {
      if (err?.response?.status === 404) setNotFound(true);
      else toast.error(apiError(err, t("cd.load_failed")));
    } finally { setLoading(false); }
  }, [id, t]);

  useEffect(() => { if (user?.role === "coach") load(); }, [user, load]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const c = detail?.client || {};
  const scopes = detail?.data_sharing?.scopes || [];
  const counts = detail?.data_sharing?.counts || {};
  const topEmotions = analysis?.top_emotions || [];

  return (
    <div data-testid="coach-client-detail-page">
      <Seo title={t("cd.seo")} noindex />
      <Link to="/app/coach/client-pulse" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="cd-back">
        <ChevronLeft className="h-4 w-4" /> {t("cd.back")}
      </Link>

      {notFound ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="cd-not-found">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><Lock className="h-6 w-6" /></div>
          <p className="mt-4 font-display text-lg text-foreground">{t("cd.no_access_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("cd.no_access_sub")}</p>
        </div>
      ) : loading ? (
        <div className="space-y-4"><Skeleton className="h-20 rounded-2xl" /><Skeleton className="h-40 rounded-2xl" /><Skeleton className="h-40 rounded-2xl" /></div>
      ) : (
        <div className="space-y-5">
          {/* Header */}
          <div className="flex items-center gap-4 rounded-2xl border border-border bg-card p-5" data-testid="cd-header">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/15 text-lg font-semibold text-primary">
              {c.avatar_url ? <img src={c.avatar_url} alt="" className="h-full w-full object-cover" /> : (c.name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="truncate font-display text-2xl text-foreground">{c.name || t("cd.client_fallback")}</h1>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {c.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {c.email}</span>}
                {c.language && <span className="inline-flex items-center gap-1"><Globe className="h-3 w-3" /> {c.language}</span>}
              </div>
            </div>
          </div>

          <div className="grid gap-5 lg:grid-cols-2" data-testid="cd-workspace">
          <div className="space-y-5">
          {/* Upcoming sessions */}
          <Section icon={CalendarClock} title={t("cd.upcoming", { n: (detail?.sessions_upcoming || []).length })} testId="cd-upcoming">
            {(detail?.sessions_upcoming || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("cd.no_upcoming")}</p>
            ) : (
              <div className="space-y-2">{detail.sessions_upcoming.map((b, i) => <SessionRow key={b.id || i} b={b} t={t} />)}</div>
            )}
          </Section>

          {/* Past sessions */}
          <Section icon={CalendarCheck} title={t("cd.past", { n: (detail?.sessions_past || []).length })} testId="cd-past">
            {(detail?.sessions_past || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("cd.no_past")}</p>
            ) : (
              <div className="space-y-2">{detail.sessions_past.slice(0, 20).map((b, i) => <SessionRow key={b.id || i} b={b} t={t} />)}</div>
            )}
          </Section>

          {/* Weekly plans */}
          <Section icon={ClipboardList} title={t("cd.plans", { n: (detail?.weekly_plan_progress || []).length })} testId="cd-plans">
            {(detail?.weekly_plan_progress || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("cd.no_plans")}</p>
            ) : (
              <div className="space-y-2">
                {detail.weekly_plan_progress.map((p) => (
                  <div key={p.plan_id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{t("cd.plan_meta", { slots: p.slots_count, done: p.completions_total, week: p.week_start || t("cd.flexible") })}</p>
                    </div>
                    <span className="shrink-0 rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold text-muted-foreground">{p.status}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>
          </div>

          <div className="space-y-5">
          {/* Data sharing */}
          <Section icon={ShieldCheck} title={t("cd.sharing_title")} testId="cd-sharing">
            {!detail?.data_sharing?.granted || scopes.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("cd.no_sharing")}</p>
            ) : (
              <>
                <div className="flex flex-wrap gap-1.5">
                  {scopes.map((s) => (
                    <span key={s} className="rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-medium text-primary">{t(`cd.scope_${s}`)}</span>
                  ))}
                </div>
                {Object.keys(counts).length > 0 && (
                  <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {Object.entries(counts).map(([k, v]) => (
                      <div key={k} className="rounded-xl border border-border bg-card p-3 text-center" data-testid={`cd-count-${k}`}>
                        <p className="font-display text-xl font-semibold text-foreground">{v}</p>
                        <p className="text-[11px] text-muted-foreground">{t(`cd.count_${k}`)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </Section>

          {/* Analysis */}
          {analysis && (
            <Section icon={BarChart3} title={t("cd.analysis_title")} testId="cd-analysis">
              <div className="mb-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
                <span>{t("cd.digestions")}: <strong className="text-foreground">{analysis.total_digestions ?? 0}</strong></span>
                <span>{t("cd.transformations")}: <strong className="text-foreground">{analysis.total_reductions ?? 0}</strong></span>
                <span>{t("cd.journal")}: <strong className="text-foreground">{analysis.total_journal_entries ?? 0}</strong></span>
              </div>
              {topEmotions.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("cd.no_emotions")}</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {topEmotions.map((e) => (
                    <span key={e.label} className="rounded-full bg-secondary px-2.5 py-1 text-[11px] text-muted-foreground">{e.label} · {e.count}</span>
                  ))}
                </div>
              )}
            </Section>
          )}

          {/* WEVA reports & snapshots */}
          <Section icon={Sparkles} title={t("cd.weva", { n: (detail?.weva_reports || []).length })} testId="cd-weva">
            {(detail?.weva_reports || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("cd.no_weva")}</p>
            ) : (
              <div className="space-y-2">
                {detail.weva_reports.map((r) => (
                  <div key={r.id} className="rounded-xl border border-border bg-card p-3">
                    <p className="text-xs text-muted-foreground">{r.created_at ? shortDate(r.created_at) : ""}{r.period_days ? ` · ${t("cd.days_n", { n: r.period_days })}` : ""}</p>
                    {r.summary && <p className="mt-1 line-clamp-3 text-sm text-foreground">{r.summary}</p>}
                  </div>
                ))}
              </div>
            )}
            {(detail?.weva_snapshots || []).length > 0 && (
              <p className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground"><Camera className="h-3.5 w-3.5" /> {t("cd.snapshots_n", { n: detail.weva_snapshots.length })}</p>
            )}
          </Section>

          {/* WEVA snapshot comparison */}
          <SnapshotCompare clientId={id} t={t} />

          {/* EVA AI nudge draft */}
          <EvaNudge clientId={id} t={t} />

          {/* Shared attachments */}
          {sharedInfo && (sharedInfo.attachments || []).length > 0 && (
            <Section icon={Paperclip} title={t("cd.attachments", { n: sharedInfo.attachment_count })} testId="cd-attachments">
              <div className="space-y-2">
                {sharedInfo.attachments.map((a, i) => (
                  <div key={i} className="rounded-xl border border-border bg-card p-3">
                    <p className="text-sm font-medium text-foreground">{a.offering_title || a.kind}</p>
                    <p className="text-xs text-muted-foreground">{a.kind}{a.scheduled_at ? ` · ${shortDate(a.scheduled_at)}` : ""}</p>
                    {a.note && <p className="mt-1 text-xs text-muted-foreground">„{a.note}“</p>}
                  </div>
                ))}
              </div>
            </Section>
          )}
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
